// API service - completely recoded for reliability

// Helper functions for token management
const getToken = () => {
  return localStorage.getItem('token');
};

const removeToken = () => {
  localStorage.removeItem('token');
};

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // If we're on the server (production), use relative API path through nginx
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Production domains - use relative path (nginx proxies /api to backend)
    if (hostname === 'x.cashboxc.com' || hostname === '72.60.101.131' || (hostname !== 'localhost' && hostname !== '127.0.0.1')) {
      return '/api';
    }
  }
  // Default to localhost for development
  return 'http://localhost:5001/api';
};

const API_BASE_URL = getApiBaseUrl();

console.log(`🌐 API Base URL: ${API_BASE_URL}`);

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  // Handle body - stringify if it's an object and not already a string
  if (config.body && typeof config.body !== 'string') {
    config.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetch(url, config);

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error(`❌ [API] Non-JSON response from ${endpoint}:`, text);
      throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!response.ok) {
      // Handle 401 Unauthorized
      if (response.status === 401) {
        // Don't auto-logout for verify endpoint - let AuthContext handle it
        if (!endpoint.includes('/auth/verify')) {
          removeToken();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        throw new Error(data.message || 'Unauthorized');
      }
      throw new Error(data.message || `Server error: ${response.status} ${response.statusText}`);
    }

    return data;
  } catch (error) {
    // Network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`❌ [API] Network error calling ${endpoint}:`, error.message);
      throw new Error('Cannot connect to server. Please make sure the backend is running and accessible.');
    }

    // Re-throw other errors
    throw error;
  }
};

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    return response;
  },

  verify: async () => {
    try {
      const response = await apiRequest('/auth/verify');
      return response;
    } catch (error) {
      // Silently handle verify errors - don't log to console
      throw error;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await apiRequest('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    return response;
  },

  changeUsername: async (newUsername, password) => {
    const response = await apiRequest('/auth/change-username', {
      method: 'PUT',
      body: JSON.stringify({ newUsername, password }),
    });
    return response;
  },
};

// Notes API
export const notesAPI = {
  // Get all notes
  getAll: async () => {
    console.log(`📥 [API] Getting all notes...`);
    const response = await apiRequest('/notes');
    console.log(`✅ [API] Got ${Array.isArray(response) ? response.length : 0} notes`);
    return response;
  },

  // Get note by ID
  getById: async (noteId) => {
    console.log(`📥 [API] getById(${noteId}) - Starting request...`);
    try {
      const response = await apiRequest(`/notes/${noteId}`);
      console.log(`✅ [API] getById(${noteId}) - Response received`);
      return response;
    } catch (error) {
      console.error(`❌ [API] getById(${noteId}) - Error:`, error);
      throw error;
    }
  },

  // Create new note
  create: async (noteName) => {
    console.log(`📝 [API] create(${noteName}) - Starting create...`);
    try {
      const response = await apiRequest('/notes', {
        method: 'POST',
        body: JSON.stringify({ noteName }),
      });
      console.log(`✅ [API] create(${noteName}) - Note created with ID: ${response.noteId}`);
      return response;
    } catch (error) {
      console.error(`❌ [API] create(${noteName}) - Error:`, error);
      throw error;
    }
  },

  // Update note
  update: async (noteId, noteName, content, pageSettings = null) => {
    console.log(`💾 [API] update(${noteId}) - Starting update...`);
    try {
      const response = await apiRequest(`/notes/${noteId}`, {
        method: 'PUT',
        body: JSON.stringify({ noteName, content, pageSettings }),
      });
      console.log(`✅ [API] update(${noteId}) - Update successful`);
      return response;
    } catch (error) {
      console.error(`❌ [API] update(${noteId}) - Error:`, error);
      throw error;
    }
  },

  // Delete note
  delete: async (noteId) => {
    console.log(`🗑️ [API] delete(${noteId}) - Starting delete...`);
    const response = await apiRequest(`/notes/${noteId}`, {
      method: 'DELETE',
    });
    console.log(`✅ [API] delete(${noteId}) - Delete successful`);
    return response;
  },
};

// Table API - COMPLETELY RECODED
export const tableAPI = {
  // Get all tables
  getAll: async () => {
    console.log(`📥 [API] Getting all tables...`);
    const response = await apiRequest('/tables');
    console.log(`✅ [API] Got ${Array.isArray(response) ? response.length : 0} tables`);
    return response;
  },

  // Get table by ID - COMPLETELY RECODED
  getById: async (tableId) => {
    console.log(`📥 [API] getById(${tableId}) - Starting request...`);
    try {
      const response = await apiRequest(`/tables/${tableId}`);
      console.log(`📥 [API] getById(${tableId}) - Response received:`, response);
      console.log(`📥 [API] getById(${tableId}) - Type:`, typeof response, 'IsArray:', Array.isArray(response));
      
      if (!response) {
        console.log(`⚠️ [API] getById(${tableId}) - Empty response, returning []`);
        return [];
      }

      // Check if response is the data array directly
      if (Array.isArray(response)) {
        console.log(`✅ [API] getById(${tableId}) - Returning array with ${response.length} rows`);
        return response;
      }

      // Check if response has a data property
      if (response.data && Array.isArray(response.data)) {
        console.log(`✅ [API] getById(${tableId}) - Returning response.data with ${response.data.length} rows`);
        return response.data;
      }

      // If response is an object but not an array, log it
      console.warn(`⚠️ [API] getById(${tableId}) - Unexpected response format:`, response);
      return [];
    } catch (error) {
      console.error(`❌ [API] getById(${tableId}) - Error:`, error);
      throw error;
    }
  },

  // Save table data - COMPLETELY RECODED
  save: async (tableId, data, tableName = '') => {
    console.log(`💾 [API] save(${tableId}) - Starting save...`);
    console.log(`💾 [API] save(${tableId}) - Data:`, Array.isArray(data) ? `${data.length} rows` : typeof data);
    
    if (!Array.isArray(data)) {
      console.error(`❌ [API] save(${tableId}) - Data is not an array!`);
      throw new Error('Data must be an array');
    }

    if (data.length === 0) {
      console.error(`❌ [API] save(${tableId}) - Empty array!`);
      throw new Error('Cannot save empty array');
    }

    try {
      const response = await apiRequest(`/tables/${tableId}`, {
        method: 'PUT',
        body: JSON.stringify({ data, tableName }),
      });
      
      console.log(`✅ [API] save(${tableId}) - Save successful`);
      console.log(`📊 [API] save(${tableId}) - Response:`, response);
      
      // Return the data that was saved
      return data;
    } catch (error) {
      console.error(`❌ [API] save(${tableId}) - Error:`, error);
      throw error;
    }
  },

  // Delete table
  delete: async (tableId) => {
    console.log(`🗑️ [API] delete(${tableId}) - Starting delete...`);
    const response = await apiRequest(`/tables/${tableId}`, {
      method: 'DELETE',
    });
    console.log(`✅ [API] delete(${tableId}) - Delete successful`);
    return response;
  },
};

// Files API
export const filesAPI = {
  // Get all files
  getAll: async () => {
    console.log(`📥 [API] Getting all files...`);
    const response = await apiRequest('/files');
    console.log(`✅ [API] Got ${Array.isArray(response) ? response.length : 0} files`);
    return response;
  },

  // Get file by ID
  getById: async (fileId) => {
    console.log(`📥 [API] getById(${fileId}) - Starting request...`);
    try {
      const response = await apiRequest(`/files/${fileId}`);
      console.log(`✅ [API] getById(${fileId}) - Response received`);
      return response;
    } catch (error) {
      console.error(`❌ [API] getById(${fileId}) - Error:`, error);
      throw error;
    }
  },

  // Upload file
  upload: async (file) => {
    console.log(`📤 [API] upload(${file.name}) - Starting upload...`);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      const url = `${API_BASE_URL}/files`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || `Upload failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ [API] upload(${file.name}) - Upload successful`);
      return data;
    } catch (error) {
      console.error(`❌ [API] upload(${file.name}) - Error:`, error);
      throw error;
    }
  },

  // Download file
  download: async (fileId, originalName) => {
    console.log(`📥 [API] download(${fileId}) - Starting download...`);
    try {
      const token = getToken();
      const url = `${API_BASE_URL}/files/${fileId}/download`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Download failed' }));
        throw new Error(errorData.message || `Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = originalName || `file-${fileId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log(`✅ [API] download(${fileId}) - Download successful`);
      return true;
    } catch (error) {
      console.error(`❌ [API] download(${fileId}) - Error:`, error);
      throw error;
    }
  },

  // Delete file
  delete: async (fileId) => {
    console.log(`🗑️ [API] delete(${fileId}) - Starting delete...`);
    const response = await apiRequest(`/files/${fileId}`, {
      method: 'DELETE',
    });
    console.log(`✅ [API] delete(${fileId}) - Delete successful`);
    return response;
  },
};

export default { authAPI, tableAPI, notesAPI, filesAPI };
